import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  HostBinding,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  UntypedFormControl,
} from '@angular/forms';
import { dynamicElementsClickableAmountInput } from '@epicuro-next/constants/dynamic-elements';
import { DynamicComponentRegister } from '@epicuro-next/platform/dynamic-components-registrator';
import { isNullish } from '@epicuro-next/utilities';
import { BehaviorSubject, interval, Subscription } from 'rxjs';
import { map, sampleTime } from 'rxjs/operators';

const CONTINUES_CHANGE_INTERVAL = 200;

enum ContiniouesChangeType {
  INCREASE = 'increase',
  DECREASE = 'decrease',
}

@DynamicComponentRegister(dynamicElementsClickableAmountInput)
@Component({
  selector: 'atx-clikable-amount-input',
  templateUrl: './clikable-amount-input.component.html',
  styleUrls: ['./clikable-amount-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ClikableAmountInputComponent),
      multi: true,
    },
  ],
  standalone: false,
})
export class ClikableAmountInputComponent
  implements OnChanges, OnDestroy, ControlValueAccessor
{
  /**
   * The initial value for the input. (Used only to get value inside DynamicItem config)
   */
  public initialValue$ = new BehaviorSubject<number>(0);
  /**
   * The initial value for the input
   */
  @Input() public initialValue = 0;

  @Input() public currentValue: number;
  /** beaware you should not useTemplateForm=true and
   * at the same time provide [fromControl] to the component
   */
  @Input() public useTemplateForm = false;

  /**
   * The string to display as a placeholder
   */
  @Input() public isInputDisabled: boolean | null;

  // Use with dynamic children to show input (!ref.innerHTML.trim() from template condition always passes as children wrapped in collection renderer)
  @Input() public forceInputShow = false;

  /**
   * Disabled state
   */
  @Input()
  public set isDisabled(value: boolean) {
    this.disabled = value;
  }
  public get isDisabled() {
    return this.disabled;
  }

  /**
   * Maximum value to disable increase
   */
  @Input() public max = Number.MAX_SAFE_INTEGER;

  @Input() public min = 0;

  /**
   * Small button and input
   */
  @Input() public compactView = false;

  /**
   * Event that emits the value when it changes
   */
  @Output() public valueChange = new EventEmitter<number>();

  @HostBinding('class')
  public get classList() {
    let classList = 'border';
    if (this.isDisabled) classList += ' disabled';
    if (this.compactView) classList += ' compactView';
    return classList;
  }

  public inputControl = new UntypedFormControl(this.initialValue);

  private continuesValueChangeSub?: Subscription;
  private disabled: boolean;
  public ContiniouesChangeType = ContiniouesChangeType;

  /**
   * Listens for value changes and emits them after 500ms
   */
  private changesSub = this.inputControl.valueChanges
    .pipe(sampleTime(500))
    .subscribe((value: number) => {
      this.currentValue = value;
      this.valueChange.emit(value);
      this.currentValue = value;
      this.onChange(value);
      this.onTouch(value);
    });

  constructor(private cdr: ChangeDetectorRef) {}

  public ngOnChanges(changes: SimpleChanges) {
    if (
      changes.initialValue &&
      changes.initialValue.currentValue !== changes.initialValue.previousValue
    ) {
      this.initialValue$.next(changes.initialValue.currentValue);
      this.inputControl.setValue(changes.initialValue.currentValue, {
        emitEvent: false,
      });
      return;
    }

    if (
      this.useTemplateForm &&
      changes.currentValue?.currentValue !==
        changes.currentValue?.previousValue &&
      changes.currentValue?.currentValue != null
    ) {
      this.inputControl.setValue(changes.currentValue.currentValue, {
        emitEvent: false,
      });
      return;
    }
  }

  public ngOnDestroy(): void {
    this.changesSub.unsubscribe();
    this.clearContinuesValueChange();
  }

  public onChange: (arg: number) => void = () => {};

  public onTouch: (arg: number) => void = () => {};

  public writeValue(value: number): void {
    this.inputControl.setValue(value, { emitEvent: false });
    this.cdr.markForCheck();
  }

  public setDisabledState(isDisabled: boolean) {
    this.isDisabled = isDisabled;
  }

  public registerOnChange(fn: (arg: number) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: (arg: number) => void): void {
    this.onTouch = fn;
  }

  public onIncrease(event: Event) {
    event.preventDefault();
    this.increase();
  }

  public onDecrease(event: Event) {
    event.preventDefault();
    this.decrease();
  }

  public increase(value: number = 1) {
    this.inputControl.setValue(
      Math.min(this.inputControl.value + value, this.max),
    );
  }

  public decrease(value: number = 1) {
    this.inputControl.setValue(
      Math.max(this.inputControl.value - value, this.min),
    );
  }

  public startContinuesChange(_: Event, type: ContiniouesChangeType) {
    this.clearContinuesValueChange();

    this.continuesValueChangeSub = interval(CONTINUES_CHANGE_INTERVAL)
      .pipe(
        map((__, index) => {
          if (index > 19) return 100;
          if (index > 9) return 10;
          return 1;
        }),
      )
      .subscribe((value) => {
        if (type === ContiniouesChangeType.INCREASE) this.increase(value);
        else this.decrease(value);
        this.cdr.detectChanges();
      });
  }

  public clearContinuesValueChange(_?: Event) {
    if (this.continuesValueChangeSub) {
      this.continuesValueChangeSub.unsubscribe();
    }
    this.continuesValueChangeSub = undefined;
  }

  public onInputBlur() {
    if (isNullish(this.inputControl.value)) {
      this.writeValue(0);
    }
  }
}
